import { PayloadAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import {
    ITableColumnSizing,
    ITableFilters,
} from '../../components/Entity/interfaces'
import { fetchColumnsSelections } from '../../services/entityServices'
import { columsnToExcludeInEntityListing } from '../../constants/constants'

export type IAttributeType = 'self' | 'relation'
export interface ColumnOrderListType {
    id: string
    name: string
    selected: boolean
    columnSelection: boolean
    attributeName: string
    columnName: string
    attributeType: IAttributeType | string //Remove this string later by resolving the errors in other places
    dataType: string
    searchable: string
    attributeSourceType?: null | 'Static' | 'Dynamic'
    unit?: string
    relationType?: string | null
}

interface EntityType {
    entityColumns: ColumnOrderListType[]
    allTableFilters: ITableFilters[] // To store filters for different entity tables
    allTableColumnSizing: ITableColumnSizing[]
    /**
     * Saved vertical scroll offset per table, keyed by the table's
     * `tableIdentifier`. Keyed (not a single value) so one table — e.g. the
     * Shipments list — can never restore its offset onto a different table
     * such as the Alerts list (UBQ-5757).
     */
    scrollPositions: Record<string, number>
    isFetchingColumn: boolean
    columnVisibilityTemplateId: string
    columnVisibilityOrgId: string
}

const initialState: EntityType = {
    entityColumns: [],
    allTableFilters: [],
    allTableColumnSizing: [],
    scrollPositions: {},
    isFetchingColumn: false,
    columnVisibilityTemplateId: '',
    columnVisibilityOrgId: '',
}

export const getEntityListColumns = createAsyncThunk(
    'entity/getEntityListColumns',
    async (
        payload: {
            orgId: string
            templateId: string
            isAdmin: boolean
            applicationId: string //TO Do: we need to optimize this
            tabIndex: number | string //TO Do: we need to optimize this
            includeParentRelations?: boolean
        },
        { rejectWithValue },
    ) => {
        const {
            orgId,
            templateId,
            isAdmin,
            applicationId,
            tabIndex,
            includeParentRelations,
        } = payload
        try {
            const resColumns = await fetchColumnsSelections(
                templateId,
                orgId,
                includeParentRelations ?? false,
            )

            // Check if any user-defined selections exist.
            const hasSavedSelections = resColumns?.some(
                (col) => col.columnSelection,
            )

            let updatedColumns: ColumnOrderListType[] = (resColumns || [])
                .map((col) => ({
                    ...col,
                    id: col.attributeName,
                    name: col.columnName,
                    // Use saved selection if it exists, otherwise default to true.
                    selected: hasSavedSelections ? col.columnSelection : true,
                }))
                .filter((item: { name: string }) => {
                    // Exclude columns based on the current application and tab context.
                    const exclusions =
                        columsnToExcludeInEntityListing[applicationId || '']?.[
                            Number(tabIndex)
                        ]
                    return !exclusions?.includes(item.name)
                })
            const localStorageColumnsOrder = localStorage.getItem(
                `entityListColumnsOrder_${orgId}_${templateId}`,
            )
            if (localStorageColumnsOrder && !isAdmin) {
                updatedColumns =
                    JSON.parse(localStorageColumnsOrder) || updatedColumns
            }
            return updatedColumns
        } catch (e) {
            return rejectWithValue(e)
        }
    },
)

/**
 * Forgets the loaded column list AND which org/template it belonged to.
 * EntityList only fetches columns when that org/template differs from the
 * current one, so clearing the list alone leaves a stale match and the
 * listing and map never load (UBQ-5817).
 */
const clearLoadedColumns = (state: EntityType): void => {
    state.entityColumns = []
    state.columnVisibilityTemplateId = ''
    state.columnVisibilityOrgId = ''
}

const entitySlice = createSlice({
    name: 'entity',
    initialState,
    reducers: {
        setFilterList(state, action: PayloadAction<ITableFilters[]>) {
            state.allTableFilters = action.payload
        },
        updateEntityListColumns(
            state,
            action: PayloadAction<ColumnOrderListType[]>,
        ) {
            state.entityColumns = action.payload
        },
        resetEntityListColumns: clearLoadedColumns,
        setScrollPosition(
            state,
            action: PayloadAction<{ id: string; position: number }>,
        ) {
            const { id, position } = action.payload
            if (!id) return
            state.scrollPositions[id] = position
        },
        setTableColumnSizing(
            state,
            action: PayloadAction<{
                id: string
                sizing: Record<string, number>
            }>,
        ) {
            const { id, sizing } = action.payload
            const index = state.allTableColumnSizing.findIndex(
                (item) => item.id === id,
            )
            if (index !== -1) {
                state.allTableColumnSizing[index].sizing = sizing
            } else {
                state.allTableColumnSizing.push({ id, sizing })
            }
        },
    },
    extraReducers: (builder) => {
        builder.addCase(getEntityListColumns.pending, (state) => {
            state.isFetchingColumn = true
        })
        builder.addCase(getEntityListColumns.fulfilled, (state, action) => {
            state.entityColumns = action.payload || []
            state.isFetchingColumn = false
            state.columnVisibilityTemplateId = action.meta.arg.templateId
            state.columnVisibilityOrgId = action.meta.arg.orgId
        })
        builder.addCase(getEntityListColumns.rejected, (state) => {
            clearLoadedColumns(state)
            state.isFetchingColumn = false
        })
    },
})

export const {
    setFilterList,
    updateEntityListColumns,
    resetEntityListColumns,
    setScrollPosition,
    setTableColumnSizing,
} = entitySlice.actions

export default entitySlice.reducer
